import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, type OrganizationSummary } from '../lib/api';
import { useWorkspace } from '../lib/workspace';

// AccentInjector — single source of truth for --accent-* CSS variables.
//
// Priority order:
//   1. Active user's Discord accent_color (if set and passes contrast).
//      The whole app shifts to their personal colour. VIP treatment.
//   2. Active org's accentColor (white-label fallback for users with no
//      Discord accent set).
//   3. Forge defaults (the GG Anvil house orange).
//
// If the user's accent is too dark to read against the dark UI, they get
// a monochromatic black-and-white palette instead of bleeding the brand
// orange back in (Luke's call: "if someone is black, they must be ok
// with B&W").
//
// The GG Anvil logo uses hardcoded `forge-*` classes (NOT `accent-*`) so
// it stays orange-and-white regardless of palette.
//
// Mount once near the root of the authed tree (Layout.tsx).

const FORGE_DEFAULTS: Record<string, string> = {
  '--accent-50':  'var(--forge-50)',
  '--accent-100': 'var(--forge-100)',
  '--accent-200': 'var(--forge-200)',
  '--accent-300': 'var(--forge-300)',
  '--accent-400': 'var(--forge-400)',
  '--accent-500': 'var(--forge-500)',
  '--accent-600': 'var(--forge-600)',
  '--accent-700': 'var(--forge-700)',
  '--accent-800': 'var(--forge-800)',
  '--accent-900': 'var(--forge-900)',
  '--accent-950': 'var(--forge-950)',
};

// Derive the 8-stop palette from a single base hex. Lightens for 200–400,
// darkens for 600–900. Tuned a touch brighter than the conservative defaults
// so the user's colour reads clearly against the dark UI.
function deriveAccentPalette(baseHex: string): Record<string, string> {
  const base = parseHex(baseHex);
  if (!base) return FORGE_DEFAULTS;
  const lifted = mix(base, [255, 255, 255], 0.18);
  return {
    '--accent-50':  hex(mix(lifted, [255, 255, 255], 0.85)),
    '--accent-100': hex(mix(lifted, [255, 255, 255], 0.72)),
    '--accent-200': hex(mix(lifted, [255, 255, 255], 0.6)),
    '--accent-300': hex(mix(lifted, [255, 255, 255], 0.4)),
    '--accent-400': hex(mix(lifted, [255, 255, 255], 0.22)),
    '--accent-500': hex(lifted),
    '--accent-600': hex(mix(lifted, [0, 0, 0], 0.14)),
    '--accent-700': hex(mix(lifted, [0, 0, 0], 0.28)),
    '--accent-800': hex(mix(lifted, [0, 0, 0], 0.45)),
    '--accent-900': hex(mix(lifted, [0, 0, 0], 0.6)),
    '--accent-950': hex(mix(lifted, [0, 0, 0], 0.78)),
  };
}

// Pure greyscale palette — used when the user's accent is too dark to be
// visible. App goes monochromatic black-and-white instead of bleeding the
// brand orange back in.
const MONOCHROMATIC_PALETTE: Record<string, string> = {
  '--accent-50':  '#f5f5f7',
  '--accent-100': '#ededf0',
  '--accent-200': '#e5e5ea',
  '--accent-300': '#c8c8d0',
  '--accent-400': '#a0a0ac',
  '--accent-500': '#8a8a96',
  '--accent-600': '#6a6a74',
  '--accent-700': '#4c4c54',
  '--accent-800': '#30303a',
  '--accent-900': '#1f1f28',
  '--accent-950': '#15151b',
};

// Reject pure black or unparseable values; reject very-dark accents that
// would visually disappear against the near-black app bg (~0.04 lum).
function userPaletteFor(accentColor: number | null | undefined): Record<string, string> | 'monochromatic' | null {
  if (typeof accentColor !== 'number' || accentColor === 0) return null;
  const r = (accentColor >> 16) & 255;
  const g = (accentColor >> 8) & 255;
  const b = accentColor & 255;
  const lum = 0.2126 * r / 255 + 0.7152 * g / 255 + 0.0722 * b / 255;
  if (lum < 0.05) return 'monochromatic';
  const baseHex = '#' + accentColor.toString(16).padStart(6, '0');
  return deriveAccentPalette(baseHex);
}

function parseHex(s: string): [number, number, number] | null {
  const m = /^#([0-9a-f]{6})$/i.exec(s.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function mix(a: [number, number, number], b: [number, number, number], t: number): [number, number, number] {
  return [
    Math.round(a[0] + (b[0] - a[0]) * t),
    Math.round(a[1] + (b[1] - a[1]) * t),
    Math.round(a[2] + (b[2] - a[2]) * t),
  ];
}

function hex([r, g, b]: [number, number, number]): string {
  const h = (n: number) => n.toString(16).padStart(2, '0');
  return `#${h(r)}${h(g)}${h(b)}`;
}

// Build scoped --accent-* + --forge-* CSS variables for a SPECIFIC colour, so a
// single surface (e.g. another player's card) themes to THAT player's accent
// without touching the app-wide palette. Mirrors onto --forge-* too so a surface
// styled with forge-* (the player card) takes the colour as well; the GG Anvil
// logo is a static SVG so it stays orange. Returns {} when there's no usable
// accent (caller falls back to the default look).
export function accentVarsFor(
  accentColor: number | null | undefined,
  accentOverride?: string | null,
): Record<string, string> {
  let palette: Record<string, string> | null = null;
  let rgbHex: string | null = null;
  if (accentOverride && /^#[0-9a-fA-F]{6}$/.test(accentOverride)) {
    palette = deriveAccentPalette(accentOverride);
    rgbHex = accentOverride;
  } else {
    const up = userPaletteFor(accentColor);
    if (up === 'monochromatic') palette = MONOCHROMATIC_PALETTE;
    else if (up) {
      palette = up;
      if (typeof accentColor === 'number') rgbHex = '#' + accentColor.toString(16).padStart(6, '0');
    }
  }
  if (!palette) return {};
  const vars: Record<string, string> = {};
  for (const [k, v] of Object.entries(palette)) {
    vars[k] = v;
    vars[k.replace('--accent-', '--forge-')] = v;
  }
  if (rgbHex) {
    const r = parseInt(rgbHex.slice(1, 3), 16);
    const g = parseInt(rgbHex.slice(3, 5), 16);
    const b = parseInt(rgbHex.slice(5, 7), 16);
    vars['--accent-user'] = rgbHex;
    vars['--accent-rgb'] = `${r}, ${g}, ${b}`;
  }
  return vars;
}

export function AccentInjector() {
  const { activeOrgId } = useWorkspace();
  const { data: orgs } = useQuery<OrganizationSummary[]>({
    queryKey: ['orgs'],
    queryFn: api.orgs,
  });
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.me });

  const accentOverride = me?.discordProfile?.accentOverride ?? null;
  const userAccent = me?.discordProfile?.accentColor ?? null;
  const activeOrg = orgs?.find(o => o.id === activeOrgId);

  useEffect(() => {
    const root = document.documentElement;

    // Priority: user-picked override > Discord accent > org accent > forge.
    // The override is a hex string the user explicitly chose on MyCard;
    // it always wins so they get exactly the colour they picked.
    let palette: Record<string, string>;
    let activeRgbHex: string | null = null;
    if (accentOverride && /^#[0-9a-fA-F]{6}$/.test(accentOverride)) {
      palette = deriveAccentPalette(accentOverride);
      activeRgbHex = accentOverride;
    } else {
      const userPalette = userPaletteFor(userAccent);
      if (userPalette === 'monochromatic') {
        palette = MONOCHROMATIC_PALETTE;
      } else if (userPalette) {
        palette = userPalette;
        if (typeof userAccent === 'number') {
          activeRgbHex = '#' + userAccent.toString(16).padStart(6, '0');
        }
      } else if (activeOrg?.accentColor) {
        palette = deriveAccentPalette(activeOrg.accentColor);
        activeRgbHex = activeOrg.accentColor;
      } else {
        palette = FORGE_DEFAULTS;
      }
    }

    for (const [k, v] of Object.entries(palette)) {
      root.style.setProperty(k, v);
    }

    // 2026 redesign: forge-* stays platform-orange. The earlier behaviour
    // mirrored the user palette into forge-* which painted the WHOLE app —
    // we now confine user-accent to the `accent-*` namespace (icons, button
    // fills, focus rings, active indicators). Body text, headings, borders,
    // dividers stay neutral via index.css `--text-*` and `--hairline-*`.
    // The mirror block was removed deliberately. Do not re-add.

    // Expose --accent-user (raw hex) and --accent-rgb (R,G,B triplet) for
    // glow/gradient/ember effects. Sourced from whichever colour won the
    // priority above (override > Discord > org > none).
    if (activeRgbHex) {
      const r = parseInt(activeRgbHex.slice(1, 3), 16);
      const g = parseInt(activeRgbHex.slice(3, 5), 16);
      const b = parseInt(activeRgbHex.slice(5, 7), 16);
      const lum = 0.2126 * r / 255 + 0.7152 * g / 255 + 0.0722 * b / 255;
      if (lum >= 0.05) {
        root.style.setProperty('--accent-user', activeRgbHex);
        root.style.setProperty('--accent-rgb', `${r}, ${g}, ${b}`);
      } else {
        root.style.setProperty('--accent-rgb', '160, 160, 172');
        root.style.removeProperty('--accent-user');
      }
    } else {
      root.style.removeProperty('--accent-user');
      root.style.removeProperty('--accent-rgb');
    }

    return () => {
      for (const k of Object.keys(FORGE_DEFAULTS)) {
        root.style.removeProperty(k);
      }
      root.style.removeProperty('--accent-user');
    };
  }, [activeOrg, userAccent, accentOverride]);

  return null;
}
